import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';

import {
  CheckEmailAddressPayload,
  CheckEmailAddressResponse,
  DigitalAccount,
  GetDigitalAccountByCdhIdResponse,
  GetDigitalAccountByWaidResponse
} from '../+state/digital-account/digital-account.model';
import { DigitalAccountAdapter } from './adapters/digital-account.adapter';

@Injectable({
  providedIn: 'root'
})
export class DigitalAccountService {
  private http = inject(HttpClient);
  private digitalAccountAdapter = inject(DigitalAccountAdapter);
  private feature = inject(FeatureFlagService);

  private digitalAccountExpURL = env.userAccountApi + '/digitalaccounts';

  getDigitalAccountByWaid(waid: string | undefined): Observable<DigitalAccount> {
    if (!waid) {
      return throwError(() => new Error('WAID is required but was not provided'));
    }

    let endpoint: string;
    if (this.feature.isEnabled('digitalaccount_exp_api')) {
      endpoint = this.digitalAccountExpURL + '/' + waid;
    } else {
      endpoint = env.digitalAccountApi + '/digitalaccounts/' + waid;
    }

    return this.http
      .get<GetDigitalAccountByWaidResponse>(endpoint)
      .pipe(
        map((response: GetDigitalAccountByWaidResponse) =>
          this.digitalAccountAdapter.adaptGetDigitalAccountByWaid(response)
        )
      );
  }

  /**
   * @param customerId - Customer ID
   * https://intssg.amfam.com/digitalaccount/v1/digitalaccount?customerId=<partyId || cdhid>
   */
  getDigitalAccountByCdhId(cdhId: string): Observable<DigitalAccount | null> {
    let endpoint: string;
    const params = new HttpParams().set('customerId', cdhId);
    if (this.feature.isEnabled('digitalaccount_exp_api')) {
      endpoint = this.digitalAccountExpURL;
    } else {
      endpoint = env.digitalAccountApi + '/digitalaccounts';
    }

    return this.http
      .get<GetDigitalAccountByCdhIdResponse>(endpoint, { params })
      .pipe(
        map((response: GetDigitalAccountByCdhIdResponse) =>
          this.digitalAccountAdapter.adaptGetDigitalAccountByCdhId(response, cdhId)
        )
      );
  }

  /**
   * @param EmailId - Email ID
   * https://intssg.amfam.com/digitalaccount/v1/digitalaccount?email=<partyId || cdhid>
   */
  getDigitalAccountByEmailId(email: string): Observable<DigitalAccount | null> {
    let endpoint: string;
    const params = new HttpParams().set('emailAddress', email).set('partnerId', 'AFI');
    if (this.feature.isEnabled('digitalaccount_exp_api')) {
      endpoint = this.digitalAccountExpURL;
    } else {
      endpoint = env.digitalAccountApi + '/digitalaccounts/';
    }

    return this.http
      .get<GetDigitalAccountByCdhIdResponse>(endpoint, { params })
      .pipe(
        map((response: GetDigitalAccountByCdhIdResponse) =>
          this.digitalAccountAdapter.adaptGetDigitalAccountByEmail(response, email)
        )
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateDigitalAccount(digitalAccount: DigitalAccount, waid: string | undefined): Observable<any> {
    if (!waid) {
      return throwError(() => new Error('WAID is required but was not provided'));
    }

    let endpoint: string;

    if (this.feature.isEnabled('digitalaccount_exp_api')) {
      endpoint = this.digitalAccountExpURL + '/' + waid;
    } else {
      endpoint = env.digitalAccountApi + '/digitalaccounts/' + waid;
    }
    return this.http.put(endpoint, { digitalAccount });
  }

  /**
   * This function is built for myaccount-admin enrollment flow, to verify if the email is free or attached to an existing myaccount
   * @param email
   * @param partnerId
   * @returns
   */
  checkEmailAddress(payload: CheckEmailAddressPayload): Observable<CheckEmailAddressResponse> {
    let endpoint: string;

    if (this.feature.isEnabled('digitalaccount_exp_api')) {
      endpoint = this.digitalAccountExpURL + '/emails';
    } else {
      endpoint = env.digitalAccountApi + '/emails';
    }

    // DM: Cannot use HttpParams since it unencodes certain special characters
    endpoint += '?emailAddress=' + encodeURIComponent(payload.emailAddress);
    endpoint += payload.customerGroupId
      ? '&customerGroupId=' + encodeURIComponent(payload.customerGroupId)
      : '';
    return this.http
      .get<CheckEmailAddressResponse>(endpoint)
      .pipe(
        map(response =>
          this.digitalAccountAdapter.adaptCheckEmailAddress(response, payload.correlationId)
        )
      );
  }
}
